import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Route, MapPin, ArrowLeft, Clock, Ruler } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AssignedRoute {
  id: string;
  name: string;
  origin_zone_name?: string;
  destination_zone_name?: string;
  distance_km?: number;
  estimated_duration_minutes?: number;
  is_active: boolean;
}

interface DriverAssignedRoutesProps {
  driverProfileId: string;
}

export function DriverAssignedRoutes({ driverProfileId }: DriverAssignedRoutesProps) {
  const { get, loading } = useApi();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState<AssignedRoute[]>([]);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (driverProfileId) {
      fetchAssignedRoutes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverProfileId]);

  const fetchAssignedRoutes = async () => {
    try {
      const result: any = await get(`/api/v1/settings/routes/driver/${driverProfileId}`);
      const routesData = result?.list || result?.data?.list || result?.data || result || [];
      setRoutes(Array.isArray(routesData) ? routesData : []);
    } catch (error) {
      console.error('Failed to fetch assigned routes:', error);
      setRoutes([]);
    } finally {
      setHasFetched(true);
    }
  };

  const handleViewRoute = (routeId: string) => {
    navigate(`/dashboard/settings/routes/${routeId}`);
  };

  if (loading && !hasFetched) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Route className="h-5 w-5" />
            المسارات المعينة
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingSkeleton type="card" rows={3} />
        </CardContent>
      </Card>
    );
  }

  if (hasFetched && routes.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <EmptyState
            icon={<Route className="h-12 w-12" />}
            title="لا توجد مسارات معينة"
            description="لم يتم تعيين أي مسارات لهذا السائق بعد"
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Route className="h-5 w-5" />
          المسارات المعينة
          <Badge variant="secondary" className="ms-2">
            {routes.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {routes.map((route) => (
            <div
              key={route.id}
              className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 shrink-0">
                  <Route className="h-5 w-5 text-primary" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium truncate">{route.name}</span>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-xs shrink-0",
                        route.is_active
                          ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800"
                          : "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800"
                      )}
                    >
                      {route.is_active ? 'نشط' : 'غير نشط'}
                    </Badge>
                  </div>
                  
                  {(route.origin_zone_name || route.destination_zone_name) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" />
                      <span className="truncate">
                        {route.origin_zone_name || '—'}
                      </span>
                      <ArrowLeft className="h-3.5 w-3.5 rotate-180" />
                      <span className="truncate">
                        {route.destination_zone_name || '—'}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                    {route.distance_km !== undefined && (
                      <span className="flex items-center gap-1">
                        <Ruler className="h-3 w-3" />
                        {route.distance_km} كم
                      </span>
                    )}
                    {route.estimated_duration_minutes !== undefined && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {route.estimated_duration_minutes} دقيقة
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleViewRoute(route.id)}
                className="shrink-0"
              >
                عرض
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
